import { type ApiResponse, estClient } from './estClient';

/**
 * - empty — targets new leads from all sources
 * - phone — targets leads from phone source
 * - object — targets leads with interest by object
 * - object_phone — targets leads with interest by object from phone source
 * - dismissed — targets leads that was assigned to manager that currently fired (User.is_disabled)
 * - easybusy_chat — targets new leads from messenger chats; falls back to the default queue when off
 */
export type RequestsContext =
  | ''
  | 'phone'
  | 'object'
  | 'object_phone'
  | 'dismissed'
  | 'easybusy_chat';

export interface Roulette {
  id: string;

  /**
   * CONTEXT_ID_DEFAULT = 0;
   * CONTEXT_ID_PHONE = 1;
   * CONTEXT_ID_OBJECT = 2;
   * CONTEXT_ID_OBJECT_PHONE = 3;
   * CONTEXT_ID_DISMISSED = 4;
   * CONTEXT_ID_EASYBUSY_CHAT = 5;
   */
  type: '0' | '1' | '2' | '3' | '4' | '5';

  /**
   * Whether this roulette enabled
   */
  mode: '0' | '1';

  /**
   * An id of an object (project) this roulette is bound to
   *
   * This field is used only for `type` 2 (object) or 3 (object phone)
   */
  object_id: string | null;

  is_synced: '1' | '0';

  /**
   * A json of an array of user ids between which requests should be distributed
   */
  user_list_json: string;

  next_index: string;

  is_send_email: '1' | '0';
  is_email_cc_to_creator: '1' | '0';
  email_list_main: string;
  email_list_secondary: string;
  lead_custom_fields: string; // json
}

export interface RouletteListResponse extends ApiResponse {
  roulette: Roulette[];
}

export interface RequestMode {
  request_que_context: RequestsContext;

  /**
   * Whether the request queue is enabled for current context
   */
  request_que_mode: '1' | '0' | null;

  /**
   * User ids list to use in queue for requests (leads) distribution
   */
  request_que: string[];

  request_que_next_id: number;
  request_custom_fields: [];
  request_email_config: {
    request_email_status: '1' | '0';
    request_email_address_main: string;
    request_email_addresses_secondary: string | '';
    request_email_cc_to_creator: 1 | 0;
  };
}

class RequestsApi {
  async getRouletteList(): Promise<Roulette[]> {
    const resp = await estClient.get<RouletteListResponse>('/api/db/roulette');

    return resp.roulette;
  }

  /**
   * @deprecated use getRouletteList() instead
   */
  getRequestsMode({
    context,
    objectId,
  }: {
    context: RequestsContext;
    objectId?: string;
  }): Promise<RequestMode> {
    if (context.includes('object') && !objectId) {
      throw new Error(`Object id is required for "${context}" context`);
    }

    return estClient.get<RequestMode>('/api/request/mode', {
      query: {
        request_que_context: context,
        object_id: objectId,
      },
    });
  }

  setRequestsMode({
    context,
    enabled,
    objectId,
  }: {
    context: RequestsContext;
    enabled: boolean;
    // TODO: it would be better to use overloads to improve typings but leave it for future
    objectId?: string;
  }): Promise<RequestMode> {
    if (context.includes('object') && !objectId) {
      throw new Error(`Object id is required for "${context}" context`);
    }

    return estClient.post<RequestMode>('/api/request/mode', {
      query: {
        request_que_context: context,
        object_id: objectId,
      },
      body: {
        request_que_mode: enabled ? 1 : 0,
      },
    });
  }

  setManagers({
    context,
    userIds,
    objectId,
  }: {
    context: RequestsContext;
    userIds: string[];
    // TODO: it would be better to use overloads to improve typings but leave it for future
    objectId?: string;
  }): Promise<RequestMode> {
    if (context.includes('object') && !objectId) {
      throw new Error(`Object id is required for "${context}" context`);
    }

    return estClient.post<RequestMode>('/api/request/managers', {
      query: {
        request_que_context: context,
        object_id: objectId,
      },
      body: {
        user_id: userIds,
      },
    });
  }

  get CONTEXT_ID() {
    return {
      DEFAULT: '0',
      PHONE: '1',
      OBJECT: '2',
      OBJECT_PHONE: '3',
      DISMISSED: '4',
      EASYBUSY_CHAT: '5',
    } as const;
  }
}

export const requestsApi = new RequestsApi();
